import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts'
import { Loader2, Activity } from 'lucide-react'
import { managedDevicesApi } from '../../../../services/managedDevicesApi'

const RANGES = [
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
]

// Cores estáveis por field — hash simples para reutilizar a mesma cor entre re-fetches.
const PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
]
const colorFor = (field) => {
  let h = 0
  for (let i = 0; i < field.length; i++) h = (h * 31 + field.charCodeAt(i)) >>> 0
  return PALETTE[h % PALETTE.length]
}

export default function MetricsHistorySection({ deviceId, currentMetricsJson }) {
  const { t } = useTranslation()
  const [range, setRange] = useState('1h')
  const [history, setHistory] = useState({ availableFields: [], series: [] })
  const [loading, setLoading] = useState(false)
  const [enabledFields, setEnabledFields] = useState(null) // null = inicial; depois Set<string>

  // Campos disponíveis: união entre os retornados pelo histórico e os presentes no
  // metricsJson atual (para já mostrar checkboxes mesmo antes de existir histórico).
  const availableFields = useMemo(() => {
    const fromHistory = new Set(history.availableFields || [])
    const fromCurrent = new Set(extractFlatKeys(currentMetricsJson))
    return Array.from(new Set([...fromCurrent, ...fromHistory])).sort()
  }, [history.availableFields, currentMetricsJson])

  // Default: habilitar até 4 fields "interessantes" (cpuLoad, memoryPercent, diskPercent, latency, etc.)
  useEffect(() => {
    if (enabledFields !== null || availableFields.length === 0) return
    const preferred = ['cpuLoad', 'cpu', 'memoryPercent', 'diskPercent', 'temperature', 'latency']
    const initial = new Set()
    for (const p of preferred) if (availableFields.includes(p) && initial.size < 4) initial.add(p)
    if (initial.size === 0) for (const f of availableFields.slice(0, 3)) initial.add(f)
    setEnabledFields(initial)
  }, [availableFields, enabledFields])

  useEffect(() => {
    let cancelled = false
    if (!deviceId) return
    setLoading(true)
    managedDevicesApi.getMetricsHistory(deviceId, { range })
      .then((data) => { if (!cancelled) setHistory(data || { availableFields: [], series: [] }) })
      .catch(() => { if (!cancelled) setHistory({ availableFields: [], series: [] }) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [deviceId, range])

  // Mescla todas as séries num único array de pontos {time, [field]: value} para o LineChart
  const chartData = useMemo(() => {
    const byTime = new Map()
    for (const s of history.series || []) {
      if (enabledFields && !enabledFields.has(s.field)) continue
      for (const p of s.points || []) {
        const ts = new Date(p.timestamp).getTime()
        if (!byTime.has(ts)) byTime.set(ts, { time: ts })
        byTime.get(ts)[s.field] = p.value
      }
    }
    return Array.from(byTime.values()).sort((a, b) => a.time - b.time)
  }, [history.series, enabledFields])

  const enabledArr = useMemo(() => Array.from(enabledFields || []), [enabledFields])

  const toggleField = (field) => {
    setEnabledFields((prev) => {
      const next = new Set(prev || [])
      if (next.has(field)) next.delete(field); else next.add(field)
      return next
    })
  }

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-theme-primary" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('managedDeviceDetail.metricsTitle', 'Métricas')}
          </h3>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={`px-2 py-1 text-xs rounded border ${
                range === r.value
                  ? 'bg-theme-primary/10 border-theme-primary/40 text-theme-primary'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
        <div className="h-72 w-full">
          {loading ? (
            <div className="h-full flex items-center justify-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />{t('common.loading', 'Carregando...')}
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-400 italic">
              {t('managedDeviceDetail.metricsEmpty', 'Sem histórico para a janela selecionada.')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  scale="time"
                  tickFormatter={(t) => formatTick(t, range)}
                  stroke="#9ca3af"
                  fontSize={11}
                />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  labelFormatter={(t) => new Date(t).toLocaleString()}
                  formatter={(v) => (typeof v === 'number' ? v.toFixed(2) : v)}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {enabledArr.map((f) => (
                  <Line
                    key={f}
                    type="monotone"
                    dataKey={f}
                    stroke={colorFor(f)}
                    dot={false}
                    isAnimationActive={false}
                    strokeWidth={1.5}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-gray-100 rounded p-2 max-h-72 overflow-y-auto">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            {t('managedDeviceDetail.pens', 'Penas')}
          </p>
          {availableFields.length === 0 ? (
            <p className="text-xs text-gray-400 italic">{t('managedDeviceDetail.noFields', 'Nenhum campo coletado ainda.')}</p>
          ) : (
            <ul className="space-y-1">
              {availableFields.map((f) => {
                const checked = enabledFields ? enabledFields.has(f) : false
                return (
                  <li key={f}>
                    <label className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleField(f)}
                        className="rounded border-gray-300"
                      />
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(f) }} />
                      <span className="text-gray-700 truncate" title={f}>{f}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}

function extractFlatKeys(metricsJson) {
  if (!metricsJson) return []
  let parsed
  try { parsed = typeof metricsJson === 'string' ? JSON.parse(metricsJson) : metricsJson }
  catch { return [] }
  const keys = []
  const walk = (obj, prefix = '') => {
    if (obj == null) return
    if (typeof obj !== 'object' || Array.isArray(obj)) return
    for (const [k, v] of Object.entries(obj)) {
      const key = prefix ? `${prefix}_${k}` : k
      if (v != null && typeof v === 'object' && !Array.isArray(v)) walk(v, key)
      else if (typeof v === 'number' || typeof v === 'boolean') keys.push(key)
      else if (typeof v === 'string' && !isNaN(parseFloat(v))) keys.push(key)
    }
  }
  walk(parsed)
  return keys
}

function formatTick(t, range) {
  const d = new Date(t)
  if (range === '7d') return `${d.getMonth() + 1}/${d.getDate()}`
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
